import {
  AccordionDetails,
  AccordionSummary,
  Accordion as MuiAccordion
} from '@mui/material'
import {
  accordionDetailsStyles,
  accordionstyles
} from '@/features/menu-management/SidebarAccordion/components/AccordionContent/AccordionContent.styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  primaryColors,
  transparentColors
} from '@assets/styles/helpers/colors.styles'
import { useTranslation } from 'react-i18next'
import DynamicForm from '@/shared/components/DynamicForm/DynamicForm'
import { useLanguageFormConfig } from '@/features/menu-management/SidebarAccordion/forms/useLanguageFormConfig'
import { useEffect, useState } from 'react'
import Loader from '@/shared/components/Loader/Loader'
import { useQuery } from '@tanstack/react-query'
import {
  languagesListResponseModel,
  QUERY
} from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI.model'
import SidebarAccordionAPI from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI'
import Button from '@/shared/components/Button/Button'
import FormResetButton from '@/shared/components/DynamicForm/FormResetButton'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import styles from '../AccordionContent/AccordionContent.module.scss'
import { useLanguageAccordionStore } from '@/features/menu-management/SidebarAccordion/store/useLanguageAccordionStore'
import { useSelectedLanguagesQuery } from '../../hooks/useSelectedLanguagesQuery'

interface LanguageAccordionProps {
  accordionIndex: number
  openAccordion: number
  handleAccordionClick: (index: number) => void
}

function LanguageAccordion({
  accordionIndex,
  openAccordion,
  handleAccordionClick
}: LanguageAccordionProps) {
  const { t } = useTranslation()
  const [isDirty, setIsDirty] = useState(false)
  const handleIsDirtyChange = (isDirty: boolean) => {
    setIsDirty(isDirty)
  }
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''

  const {
    defaultLanguage,
    selectedLanguagesList,
    setDefaultLanguage,
    setSelectedLanguagesList
  } = useLanguageAccordionStore()

  const {
    selectedLanguagesListQuery,
    selectedLanguagesOptions,
    defaultLanguageOption
  } = useSelectedLanguagesQuery(currentLanguage)

  const allLanguagesList = useQuery<languagesListResponseModel, ErrorModel>({
    queryKey: [QUERY.LANGUAGES],
    queryFn: SidebarAccordionAPI.getLanguageList
  })

  const isLoading =
    allLanguagesList.isLoading || selectedLanguagesListQuery.isLoading

  const mappedAllLanguages = allLanguagesList.data?.[
    currentLanguage as 'en' | 'pl'
  ].map(language => ({
    value: language.languageCode,
    label: language.name
  }))

  const [defaultValues, setDefaultValues] = useState({
    defaultLanguageName: defaultLanguage,
    selectedLanguagesName: selectedLanguagesList
  })

  const editLanguageFromConfig = useLanguageFormConfig({
    handleIsDirtyChange,
    allLanguagesList: mappedAllLanguages || [],
    defaultValues
  })

  useEffect(() => {
    if (selectedLanguagesOptions) {
      setSelectedLanguagesList(selectedLanguagesOptions)
    }
  }, [selectedLanguagesOptions])

  useEffect(() => {
    if (defaultLanguageOption) {
      setDefaultLanguage(defaultLanguageOption)
    }
  }, [defaultLanguageOption])

  useEffect(() => {
    setDefaultValues({
      defaultLanguageName: defaultLanguage,
      selectedLanguagesName: selectedLanguagesList
    })
  }, [defaultLanguage, defaultLanguageOption])

  return (
    <MuiAccordion
      expanded={openAccordion === accordionIndex}
      onChange={() => handleAccordionClick(accordionIndex)}
      sx={{
        ...accordionstyles,
        background: transparentColors.whiteShade1
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: primaryColors.white }} />}
      >
        <p className={`${styles.accordionTitle} body-bold-medium`}>
          {t('SIDEBAR_ACCORDION.LANGUAGES.TITLE')}
        </p>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <div className={`${styles.accordionFormContainer}`}>
          <p className={`${styles.accordionDescription} body-medium`}>
            {t('LANGUAGE.SET_LANGUAGES_DESCRIPTION')}
          </p>
          {!isLoading ? (
            <DynamicForm
              {...editLanguageFromConfig}
              superCondensed
              /*
                Both buttons sit inside the form. `FormResetButton` reaches it
                through `useFormContext`, which only resolves inside the
                provider, and submit no longer needs the `form` attribute now
                that it is a descendant.
              */
              FormActions={
                <div className={styles.accordionFormActions}>
                  <Button
                    maxWidth
                    label={t('SIDEBAR_ACCORDION.LANGUAGES.UPDATE')}
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!isDirty}
                  />
                  <FormResetButton maxWidth />
                </div>
              }
            />
          ) : (
            <Loader size="medium" />
          )}
        </div>
      </AccordionDetails>
    </MuiAccordion>
  )
}
export default LanguageAccordion
